import { graphqlPost } from "api/graphqlClient";
import StatusBadge from "components/StatusBadge/StatusBadge";
import Tooltip from "components/Tooltip/Tooltip";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { getRelativeUrl } from "utils/service";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { StatusMatrixWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";
import {
    cellColor,
    cellTooltip,
    MatrixCell as MatrixCellValue,
    MatrixValue,
    selectGroups,
} from "./statusMatrixState";

const POLL_INTERVAL_MS = 30000;

interface MatrixSetting {
    id: string;
    name: string;
    value?: MatrixValue | null;
}

/**
 * Resolve a cell link to an in-app path (NavLink) or an external http(s) URL.
 * Any other URL scheme is ignored so a setting value cannot inject a
 * `javascript:` link.
 */
const resolveCellLink = (cell: MatrixCellValue): { path?: string; href?: string } => {
    const link = cell.link;
    if (!link) {
        return {};
    }
    if (link.record_type && link.id) {
        return { path: getRelativeUrl(link.record_type as any, link.id, "id") };
    }
    if (link.url) {
        if (link.url.startsWith("/")) {
            return { path: link.url };
        }
        if (/^https?:\/\//i.test(link.url)) {
            return { href: link.url };
        }
    }
    return {};
};

/** One matrix cell: a coloured status pill, optionally a link. */
const MatrixCell = ({ cell }: { cell: MatrixCellValue }) => {
    const badge = <StatusBadge type={cellColor(cell)} round size="small" text="" />;
    const tooltip = cellTooltip(cell);
    const { path, href } = resolveCellLink(cell);
    // A linked pill is a NAVIGATION link (affordance-interactivity principle):
    // render it as a real link so it can be middle/right-clicked.
    let inner;
    if (path) {
        inner = (
            <NavLink
                className="DashboardStatusMatrix__cell DashboardStatusMatrix__cell--link"
                to={path}
            >
                {badge}
            </NavLink>
        );
    } else if (href) {
        inner = (
            <a
                className="DashboardStatusMatrix__cell DashboardStatusMatrix__cell--link"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
            >
                {badge}
            </a>
        );
    } else {
        inner = <span className="DashboardStatusMatrix__cell">{badge}</span>;
    }
    return (
        <Tooltip overlay={tooltip} overlayStyle={{ zIndex: 20000 }}>
            {inner}
        </Tooltip>
    );
};

/**
 * `status_matrix` widget — a grid of coloured status cells.
 *
 * Rows ("items") are arranged in groups; each row holds a list of cells, each a
 * coloured status pill that can link to a record or URL.
 *
 * Reads a pre-aggregated matrix off a SETTING record's JSON `value` (whose name
 * the user configures) — so the widget makes ONE cheap query instead of many
 * live lookups. The user's own flow writes that setting. See the Dashboards
 * documentation for the JSON shape and an example generator flow.
 */
export const StatusMatrixWidget = ({ config }: WidgetComponentProps<StatusMatrixWidgetConfig>) => {
    const [value, setValue] = useState<MatrixValue | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    const prevValueRef = useRef<MatrixValue | null | undefined>(undefined);

    const settingName = config?.setting_name ?? "";
    // `modes` is the pre-1.0 name of `groups`; still honoured for stored configs.
    const configGroups = config?.groups ?? config?.modes;
    const live = config?.live ?? false;

    const fetchMatrix = useCallback(async (): Promise<boolean> => {
        if (!settingName) {
            if (isMounted.current) {
                prevValueRef.current = null;
                setValue(null);
                setError(null);
                setLoading(false);
            }
            return false;
        }
        const query = `query DashboardStatusMatrix($by: String, $id: String!) {
            setting(by: $by, id: $id) {
                id
                name
                value
            }
        }`;
        try {
            const response = await graphqlPost({
                query,
                variables: { by: "name", id: settingName },
            });
            const setting: MatrixSetting | null = response?.data?.setting ?? null;
            const next: MatrixValue | null = setting?.value ?? null;
            const changed = !isEqual(prevValueRef.current, next);
            if (isMounted.current) {
                if (changed) {
                    prevValueRef.current = next;
                    setValue(next);
                }
                setError(null);
                setLoading(false);
            }
            return changed;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load status matrix."));
                setLoading(false);
            }
            return false;
        }
    }, [settingName]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchMatrix,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchMatrix]);

    const groups = selectGroups(value, configGroups);

    let content;
    if (!settingName) {
        content = (
            <div className="DashboardWidget__empty">
                No setting configured. Set the widget&apos;s <strong>Setting name</strong> to a
                setting whose JSON value holds a status matrix.
            </div>
        );
    } else if (error && !value) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (isLoading) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (!value || groups.length === 0) {
        content = <div className="DashboardWidget__empty">No status matrix data.</div>;
    } else {
        const generatedAt = value.generated_at
            ? new Date(value.generated_at).toLocaleString()
            : null;
        const summary = [value.summary, generatedAt ? `generated ${generatedAt}` : null]
            .filter(Boolean)
            .join(" · ");
        content = (
            <div className="DashboardStatusMatrix">
                {summary && <div className="DashboardStatusMatrix__summary">{summary}</div>}
                {/* ONE table for all groups, so the cell column starts at the
                    same x in every group (the label column width is shared). */}
                <table className="DashboardStatusMatrix__table">
                    {groups.map((group) => (
                        <tbody className="DashboardStatusMatrix__group" key={group.label}>
                            <tr>
                                <th
                                    className="DashboardStatusMatrix__groupTitle"
                                    colSpan={2}
                                    scope="rowgroup"
                                >
                                    {group.label} · {group.items?.length ?? 0}
                                </th>
                            </tr>
                            {(group.items ?? []).map((item, itemIdx) => (
                                <tr
                                    className={`DashboardStatusMatrix__row${
                                        itemIdx % 2 === 1 ? " DashboardStatusMatrix__row--odd" : ""
                                    }`}
                                    key={`${item.label}-${itemIdx}`}
                                >
                                    <th
                                        className="DashboardStatusMatrix__rowHeader"
                                        scope="row"
                                        title={item.title ?? item.label}
                                    >
                                        {item.label}
                                    </th>
                                    <td className="DashboardStatusMatrix__cells">
                                        <div className="DashboardStatusMatrix__cellsInner">
                                            {(item.cells ?? []).map((cell, idx) => (
                                                // eslint-disable-next-line react/no-array-index-key
                                                <MatrixCell cell={cell} key={idx} />
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    ))}
                </table>
            </div>
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
